import React, { useEffect, useRef, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useTranslation } from "react-i18next";

import "react-tabs/style/react-tabs.css";
import { NavLink, useSearchParams } from "react-router-dom";
import {
  GetShortUserModel,
  useAppSelector,
  UserRole,
  useSignalR,
} from "@shared/ui";
import { HubConnectionState } from "@microsoft/signalr";

import {
  userService,
  droneService,
  authService,
  DroneList,
  UsersList,
  Header,
} from "@shared/ui";
import { GetShortDroneModel } from "@shared/ui";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const customer = useAppSelector((store) => store.customer.value);
  const user = useAppSelector((store) => store.user.value);
  const [drones, setDrones] = useState<GetShortDroneModel[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(0);
  const hubConnection = useSignalR();
  const [droneStatus, setDroneStatus] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [dronesOnline, setDronesOnline] = useState<{ [key: string]: boolean }>(
    {},
  );
  const timer = useRef<any>(null);
  const pageSize = 10;
  const [currentPageDrones, setCurrentPageDrones] = useState<number>(1);
  const [totalPagesDrones, setTotalPagesDrones] = useState(1);
  const [currentPageOperators, setCurrentPageOperators] = useState<number>(1);
  const [totalPagesOperators, setTotalPagesOperators] = useState(1);

  useEffect(() => {
    if (searchParams.has("tab")) {
      setSelectedTab(+searchParams.get("tab")!);
    }
  }, []);

  useEffect(() => {
    console.log("Hub connection: ", hubConnection);
    if (hubConnection) {
      hubConnection.on("OperatorConnected", onOperatorConnected);
      hubConnection.on("OperatorDisconnected", onOperatorDisconnected);
      hubConnection.on("OccupiedDrones", getOccupiedDrones);
      hubConnection.on("OnlineDrones", getOnlineDrones);
      hubConnection.on("DroneConnected", onDroneConnected);
      hubConnection.on("DroneDisconnected", onDroneDisconnected);
      hubConnection.onreconnected(async () => {
        await hubConnection.invoke("GetOccupiedDrones");
        await hubConnection.invoke("GetOnlineDrones");
      });

      return () => {
        hubConnection.off("OperatorConnected", onOperatorConnected);
        hubConnection.off("OperatorDisconnected", onOperatorDisconnected);
        hubConnection.off("OccupiedDrones", getOccupiedDrones);
        hubConnection.off("OnlineDrones", getOnlineDrones);
        hubConnection.off("DroneConnected", onDroneConnected);
        hubConnection.off("DroneDisconnected", onDroneDisconnected);
      };
    }
  }, [hubConnection]);

  useEffect(() => {
    if (hubConnection && hubConnection.state == HubConnectionState.Connected) {
      hubConnection.invoke("GetOccupiedDrones");
      hubConnection.invoke("GetOnlineDrones");
    } else {
      if (timer.current == null) {
        timer.current = setInterval(() => {
          if (
            hubConnection &&
            hubConnection.state == HubConnectionState.Connected
          ) {
            hubConnection.invoke("GetOccupiedDrones");
            hubConnection.invoke("GetOnlineDrones");
            clearInterval(timer.current);
          }
        }, 500);
      }
    }
  }, [hubConnection, hubConnection?.state]);

  const onOperatorConnected = (droneId: string) => {
    setDroneStatus((prev) => ({ ...prev, [droneId]: true }));
    console.log(`Drone ${droneId}: occupied`);
  };

  const onOperatorDisconnected = (droneId: string) => {
    setDroneStatus((prev) => ({ ...prev, [droneId]: false }));
    console.log(`Drone ${droneId}: free`);
  };

  const getOccupiedDrones = (droneIds: string[]) => {
    setDroneStatus(
      droneIds.reduce((obj: { [key: string]: boolean }, id: string) => {
        obj[id] = true;
        return obj;
      }, {}),
    );
    console.log("Occupied drones: ", droneIds);
  };

  const getOnlineDrones = (droneIds: string[]) => {
    setDronesOnline(
      droneIds.reduce((obj: { [key: string]: boolean }, id: string) => {
        obj[id] = true;
        return obj;
      }, {}),
    );
    console.log("Online drones: ", droneIds);
  };

  const onDroneConnected = (droneId: string) => {
    setDronesOnline((prev) => ({ ...prev, [droneId]: true }));
    console.log(`Drone ${droneId}: online`);
  };

  const onDroneDisconnected = (droneId: string) => {
    setDronesOnline((prev) => ({ ...prev, [droneId]: false }));
    console.log(`Drone ${droneId}: offline`);
  };

  useEffect(() => {
    if (user?.role === UserRole.Operator) {
      userService
        .getUserDrones(user.id, currentPageDrones, pageSize)
        .then((data) => {
          setDrones(data.items);
          setTotalPagesDrones(data.totalPageCount);
        })
        .catch();
    } else {
      if (customer) {
        droneService
          .getDrones(customer.id, currentPageDrones, pageSize)
          .then((data) => {
            setDrones(data.items);
            setTotalPagesDrones(data.totalPageCount);
          })
          .catch();
      }
    }
  }, [user, customer, currentPageDrones]);

  const [users, setUsers] = useState<GetShortUserModel[] | null>(null);

  useEffect(() => {
    if (customer && user?.role !== UserRole.Operator) {
      userService
        .getUsers(customer.id, currentPageOperators, pageSize)
        .then((data) => {
          setUsers(data.items.filter((u) => u.id !== user?.id));
          setTotalPagesOperators(data.totalPageCount);
        })
        .catch();
    }
  }, [user, customer, currentPageOperators]);

  const blockUser = async (userId: number) => {
    try {
      await userService.blockUser(userId);
      if (customer && user?.role !== UserRole.Operator) {
        userService
          .getUsers(customer.id, currentPageOperators, pageSize)
          .then((data) => {
            setUsers(data.items.filter((u) => u.id !== user?.id));
            setTotalPagesOperators(data.totalPageCount);
          })
          .catch();
      }
    } catch {}
  };

  const deleteDrone = (droneId: string) => {
    droneService
      .deleteDrone(droneId)
      .then((data) => {
        setDrones(data.items);
        setTotalPagesDrones(data.totalPageCount);
      })
      .catch();
  };

  const makeAdmin = (userId: number) => {
    userService
      .makeAdmin(userId)
      .then(() => {
        setUsers((users) =>
          users!.map((u) => {
            if (u.id === userId) {
              u.role = UserRole.CustomerAdmin;
            }
            return u;
          }),
        );
      })
      .catch();
  };

  const revokeAdmin = (userId: number) => {
    userService
      .revokeAdmin(userId)
      .then(() => {
        setUsers((users) =>
          users!.map((u) => {
            if (u.id === userId) {
              u.role = UserRole.Operator;
            }
            return u;
          }),
        );
      })
      .catch();
  };

  const resetPassword = async (
    userId: number,
    password: string,
    confirmPassword: string,
  ): Promise<boolean> => {
    try {
      await authService.updateUserPassword(userId, password, confirmPassword);
      return true;
    } catch {
      return false;
    }
  };

  const changeTab = (index: number) => {
    setSelectedTab(index);
    searchParams.set("tab", index.toString());
    setSearchParams(searchParams);
  };

  const handlePageChangeDrones = (page: number) => {
    setCurrentPageDrones(page);
  };

  const handlePageChangeOperators = (page: number) => {
    setCurrentPageOperators(page);
  };

  return (
    <div className="container">
      <Header />
      <Tabs selectedIndex={selectedTab} onSelect={changeTab}>
        <TabList>
          <Tab>{t("dashboard.tabs.dronesList")}</Tab>
          {user?.role !== UserRole.Operator ? (
            <Tab>{t("dashboard.tabs.operatorsList")}</Tab>
          ) : null}
        </TabList>

        <TabPanel>
          {(user?.role === UserRole.SuperAdmin || user?.role === UserRole.ServiceAdmin)? (
            <NavLink className="btn" to="/create-drone">
              {t("dashboard.buttons.addDrone")}
            </NavLink>
          ) : null}
          <DroneList
            dronesListData={drones}
            showControls={true}
            onDelete={
              user?.role === UserRole.SuperAdmin ? deleteDrone : undefined
            }
            inUse={droneStatus}
            online={dronesOnline}
            totalPages={totalPagesDrones}
            currentPage={currentPageDrones}
            handlePageChangeDrones={handlePageChangeDrones}
          />
        </TabPanel>
        {user?.role !== UserRole.Operator ? (
          <TabPanel>
            <NavLink className="btn" to="/create-user">
              {t("dashboard.buttons.addOperator")}
            </NavLink>
            <UsersList
              userList={users}
              onBlock={blockUser}
              onMakeAdmin={makeAdmin}
              onRevokeAdmin={revokeAdmin}
              onResetPassword={resetPassword}
              toViewUser={true}
              totalPages={totalPagesOperators}
              currentPage={currentPageOperators}
              handlePageChangeDrones={handlePageChangeOperators}
            />
          </TabPanel>
        ) : null}
      </Tabs>
      {/*<h1 className="text-left">Список доступных дронов:</h1>
            <DroneList dronesListData={drones} />*/}
    </div>
  );
};

export default Dashboard;
